import { useEffect, useState } from 'react'
import { messageOf } from '../../lib/errors'
import { loadMyOrders, cancelOrder, orderState, type MyOrder } from '../../lib/shop'
import { useHubLang } from '../../lib/i18n'
import { SHOP } from '../../lib/hubCopy'
import { PickupSheet } from './PickupSheet'

/**
 * What you have ordered. REC-11's other half.
 *
 * ── CANCELLABLE UP TO COLLECTION, AND THE BUTTON COMES FROM THE SERVER ──────
 *
 * `may_cancel` is computed in `my_orders()` from the order's own status, not
 * re-derived here. David, 14 Sep: cancel any time before the handover, whether
 * the box is on the shelf with your name on it or you are still in the queue —
 * the room loses nothing, the item goes back, and the next person waiting gets
 * it immediately. After collection there is nothing to give back, and the
 * server refuses; this just does not offer it.
 *
 * ── A CANCELLED ORDER STAYS ON THE LIST ─────────────────────────────────────
 *
 * It is money-shaped. Stars left the balance and came back, and a row that
 * disappears leaves somebody unable to check that the second half happened.
 * Same reasoning as 063's `edited_at`: a correction is visible, never silent.
 *
 * ── NO CONFIRM, DELIBERATELY ────────────────────────────────────────────────
 *
 * Cancelling is reversible in the only sense that matters — the stars come back
 * in full and the item can be ordered again while it is in stock. 030's
 * asymmetry: a confirm belongs on the irreversible act, and spending one here
 * trains people to click through the one that matters.
 *
 * ── PICKUP WITH CODE (REC-49, David 27 Sep) ─────────────────────────────────
 *
 * Beside Cancel, and ONLY when `my_orders()` handed back a live code — which it
 * does only for a held order whose code is unspent and unexpired. Waiting in
 * line has no code (nothing is on the shelf yet), so it has only Cancel. The
 * button opens PickupSheet; the sheet is the one place the code is drawn.
 */
export function MyOrders({
  onChanged,
  version = 0,
}: {
  onChanged: () => void | Promise<void>
  /**
   * Bumped by the route after an order is placed, so the list re-reads. Until
   * 25 Sep this read ran once on mount, so a new order did not appear here
   * until the page was reloaded — invisible while the list sat at the bottom,
   * and the whole of the confirmation now that it sits at the top.
   */
  version?: number
}) {
  const lang = useHubLang()
  const t = SHOP[lang]
  const [orders, setOrders] = useState<MyOrder[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [showing, setShowing] = useState<MyOrder | null>(null)

  async function reload() {
    setOrders(await loadMyOrders())
  }

  useEffect(() => {
    let live = true
    loadMyOrders()
      .then((o) => live && setOrders(o))
      .catch((e: Error) => live && setError(messageOf(e)))
    return () => { live = false }
  }, [version])

  async function cancel(o: MyOrder) {
    setBusy(o.orderId)
    setError(null)
    try {
      await cancelOrder(o.orderId)
      // Re-read the LIST, never the row. A cancel releases a hold, which can
      // immediately reserve the item for somebody else and change more than
      // this one row — and 030's suite recorded the sibling trap of re-reading
      // a row whose visibility the write just changed.
      await reload()
      await onChanged()
    } catch (e) {
      setError(messageOf(e as Error))
    } finally {
      setBusy(null)
    }
  }

  if (orders !== null && orders.length === 0) return null

  return (
    <section className="shop__orders">
      <h2 className="card__title">{t.yourOrders}</h2>
      {error && <p className="composer__error" role="alert">{error}</p>}
      {orders === null ? (
        <p className="empty" aria-busy="true">{t.loadingOrders}</p>
      ) : (
        <ul className="shop__orderlist">
          {orders.map((o) => (
            <OrderRow
              key={o.orderId}
              order={o}
              busy={busy === o.orderId}
              onCancel={() => cancel(o)}
              onPickup={() => setShowing(o)}
            />
          ))}
        </ul>
      )}
      <PickupSheet
        order={showing}
        onClose={() => setShowing(null)}
        onChanged={async () => {
          await reload()
          await onChanged()
        }}
      />
    </section>
  )
}

/** One order and what you can do with it. Exported so the suite can render it. */
export function OrderRow({
  order: o,
  busy = false,
  onCancel = () => {},
  onPickup = () => {},
}: {
  order: MyOrder
  busy?: boolean
  onCancel?: () => void
  onPickup?: () => void
}) {
  const lang = useHubLang()
  const t = SHOP[lang]
  return (
    <li className={`shop__order shop__order--${o.status}`}>
      <div className="shop__ordermain">
        <p className="shop__ordername">
          {o.itemName}
          {o.variantLabel && o.variantLabel !== 'Standard' && (
            <span className="shop__ordervariant"> · {o.variantLabel}</span>
          )}
        </p>
        <p className="shop__ordermeta">
          {orderState(o, lang)} · {o.roomName}, {o.roomCity} · ★{' '}
          {o.priceStars.toLocaleString()}
        </p>
      </div>
      {(o.handoverCode || o.mayCancel) && (
        <div className="shop__orderacts">
          {o.handoverCode && (
            <button type="button" className="btn btn--primary btn--xs" onClick={onPickup}>
              {t.pickupWithCode}
            </button>
          )}
          {o.mayCancel && (
            <button type="button" className="btn btn--xs" disabled={busy} onClick={onCancel}>
              {busy ? t.cancelling : t.cancel}
            </button>
          )}
        </div>
      )}
    </li>
  )
}
